// 1. ✅ useEffect ko import kiya
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Badge, Container, Pagination } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { styles } from "./blogStyles";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API_URL } from "../utils/url";

const BlogPage = () => {
  const navigate = useNavigate();

  // --- STATES ---
  const [blogs, setBlogs] = useState([]); // API Blogs data store
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // --- API FETCH FUNCTION ---
  const getAllBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/blog/get_active_blogs`);

      if (response.data.success) {
        // Backend agar data array send karta hai: { success: true, blogs: [...] }
        setBlogs(response.data.blogs || []);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllBlogs();
  }, []);

  // --- REAL-TIME PAGINATION MATH LOGIC ✅ ---
  // Blogs state variable ke upar pagination configurations calculate kiye gaye hain
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(blogs.length / itemsPerPage);

  // Page change trigger handler
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- RESPONSIVE PAGINATION ITEMS GENERATOR ---
  const renderPaginationItems = () => {
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
          style={{
            "--bs-pagination-active-bg": "#06b6d4",
            "--bs-pagination-active-border-color": "#06b6d4",
            "--bs-pagination-color": "#06b6d4",
          }}
        >
          {number}
        </Pagination.Item>,
      );
    }
    return items;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 25 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } },
  };

  if (loading) {
    return (
      <div
        style={{
          ...styles.pageBackground,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          color: "#f30f9b",
        }}
      >
        <h3>Loading blogs...</h3>
      </div>
    );
  }
  const currentCanonicalUrl = window.location.origin + window.location.pathname;
  return (
    <>
      <Helmet>
        <title>Spiritual Blogs & Bhakti Katha | Vishnu Ji Maharaj</title>
        <meta
          name="description"
          content="Explore spiritual blogs, bhakti katha, Sanatan Dharma teachings, Vishnu stories, Krishna wisdom, and devotional articles."
        />
        <meta
          name="keywords"
          content="Vishnu Ji Maharaj, Bhakti Katha, Sanatan Dharma, Spiritual Stories, Hindu Dharma, Krishna Katha, Jagannath Katha, Devotional Content"
        />
        <link rel="canonical" href={currentCanonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Spiritual Blogs & Bhakti Katha" />
        <meta
          property="og:description"
          content="Explore spiritual blogs, bhakti katha, and devotional teachings."
        />
        <meta property="og:url" content={currentCanonicalUrl} />
      </Helmet>

      <div style={styles.pageBackground}>
        <div style={styles.neonBlob1}></div>
        <div style={styles.neonBlob2}></div>

        <Container className="py-5" style={{ position: "relative", zIndex: 2 }}>
          {/* Header Section */}
          <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-5"
          >
            <h1 style={styles.mainTitle}>
              Blogs <span style={styles.gradientText}>Knowledge Hub</span>
            </h1>
            <p style={styles.subtitle}>
              Insightful reads on mindfulness, design, and spiritual lifestyle
            </p>
          </motion.header>

          {/* Animated Bootstrap Grid Container */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <Row className="g-4">
                {currentBlogs.length === 0 ? (
                  <Col xs={12} className="text-center text-white mt-5">
                    <h5>No active blogs found.</h5>
                  </Col>
                ) : (
                  currentBlogs.map((blog) => (
                    <Col key={blog._id} xs={12} md={6} lg={4}>
                      <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -10, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => navigate(`/blogs/${blog.slug}`)} // Agar key slug na ho toh ID use kar sakte hain
                        style={{ cursor: "pointer", height: "100%" }}
                      >
                        <Card style={styles.card}>
                          <div style={styles.imageContainer}>
                            <Card.Img
                              // ✅ Backend model key (blogImage.url) use ki gayi hai
                              src={
                                blog.blogImage?.url ||
                                "https://via.placeholder.com/400x250"
                              }
                              style={styles.cardImage}
                              alt={blog.title}
                              loading="lazy"
                            />
                            <Badge
                              style={{
                                ...styles.categoryBadge,
                                background:
                                  blog.gradient ||
                                  "linear-gradient(45deg, #06b6d4, #3b82f6)",
                              }}
                            >
                              {/* {blog.authorName || "Spiritual"} */}
                            </Badge>
                          </div>

                          <Card.Body style={styles.cardBody}>
                            <Card.Title style={styles.cardTitle}>
                              {blog.title}
                            </Card.Title>

                            <Card.Text style={styles.cardText}>
                              {/* description se HTML tag filter karne aur slice karne ke liye string control logic */}
                              {blog.description
                                ? blog.description
                                    .replace(/<[^>]*>/g, "")
                                    .substring(0, 120) + "..."
                                : ""}
                            </Card.Text>

                            <motion.div
                              style={{
                                ...styles.readMoreLink,
                                color: "#06b6d4",
                              }}
                              className="mt-3 d-inline-block"
                              whileHover={{ x: 5 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              Read Article &rarr;
                            </motion.div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))
                )}
              </Row>
            </motion.div>
          </AnimatePresence>

          {/* --- MOBILE RESPONSIVE PAGINATION CONTROL --- */}
          {totalPages > 1 && (
            <div
              className="d-flex justify-content-center mt-5 overflow-auto custom-pagination-wrapper"
              style={{
                maxWidth: "100%",
                padding: "10px 0",
              }}
            >
              <Pagination
                size={window.innerWidth < 576 ? "sm" : "md"}
                style={{
                  margin: 0,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />

                {renderPaginationItems()}

                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default BlogPage;
